<template>
    <div class="col-12 list-container nopads" @click.stop="">
      <h3 class="editor-header">{{ name }}</h3>
      <span class="item-detail-text">
        {{ timeRange }}
      </span>
      <div v-if="tableItems.length > 0" id="time-list" class="col-sm-12 list-content result-table">
          <vue3-datatable
              ref="resultTable"
              :rows="tableItems"
              :columns="fields"
              :pagination="false"
              :sticky-header="true"
              height="100%"
              :sortable="true"
              skin="bh-table-compact"
              :row-class="getRowClass"
              cell-class="routa-table-cell"
              @row-click="emitEdit"
          >
              <template #start="row">
                  {{ row.value.startTime }}
              </template>
              <template #approved_start="row">
                  {{ row.value.approvedStart }}
              </template>
              <template #end="row">
                  {{ row.value.endTime }}
              </template>
              <template #approved_end="row">
                  {{ row.value.approvedEnd }}
              </template>
          </vue3-datatable>
      </div>
      <div class="col-12 d-flex justify-content-end my-2">
        <button
          v-if="isObserver"
          class="form-button btn btn-sm btn-success"
          @click.stop="addRecord"
        >
          {{ $t('work_time.add_record') }}
        </button>
        <button
          v-if="isObserver && showApproval"
          class="form-button btn btn-sm btn-primary"
          @click.stop="approveRecords"
        >
          {{ $t('work_time.approve_records') }}
        </button>
        <button
          class="form-button btn btn-sm btn-secondary"
          @click.stop="$emit('closeRecords')"
        >
          {{ $t('common.close') }}
        </button>
      </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import {restApi} from '../mixins/RestApiMixin'
import {workShift} from "../mixins/WorkShift";
import {inject} from "vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";

export default {
    name: 'WorktimeList',
    components: {Vue3Datatable},
    mixins: [timeUtils, restApi, workShift],
    props: {
        results: {
            type: Array,
            default() {
                return []
            }
        },
        isObserver: {
            type: Boolean,
            default: false
        },
        showApproval: {
            type: Boolean,
            default: true
        },
        name: {
            type: String,
            default: ''
        },
        userId: {
            type: Number,
            default: -1
        },
        timeRange: {
            type: String,
            default: ''
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            tableItems: [],
            fields: [
                { field: "mode", title: this.$t('work_time.mode'), sort: true, headerClass: 'routa-table-header'},
                { field: "costCenter", title: this.$t('work_time.cost_center'), sort: true, headerClass: 'routa-table-header'},
                { field: "shift", title: this.$t('work_shift.shift'), sort: false, headerClass: 'routa-table-header'},
                { field: "contractName", title: this.$t('work_time.contract'), sort: false, headerClass: 'routa-table-header'},
                { field: "taskName", title: this.$t('work_time.task'), sort: false, headerClass: 'routa-table-header'},
                { field: "start", title: this.$t('work_time.start'), sort: true, headerClass: 'routa-table-header'},
                { field: "approved_start", title: this.$t('work_time.approved_start'), sort: true, headerClass: 'routa-table-header'},
                { field: "startLocation", title: this.$t('work_time.start_location'), sort: true, headerClass: 'routa-table-header'},
                { field: "end", title: this.$t('work_time.end'), sort: true,  headerClass: 'routa-table-header'},
                { field: "approved_end", title: this.$t('work_time.approved_end'), sort: true, headerClass: 'routa-table-header'},
                { field: "endLocation", title: this.$t('work_time.end_location'), sort: true, headerClass: 'routa-table-header'},
                { field: "duration", title: this.$t('work_time.duration'), sort: true,  type: 'number', headerClass: 'routa-table-header'},
                { field: "approver", title: this.$t('work_time.approver'), sort: true, headerClass: 'routa-table-header'},
                { field: "note", title: this.$t('work_time.note'), sort: true, headerClass: 'routa-table-header'}
            ]
        }
    },
    mounted: function () {
        this.initTableItems()
    },
    methods: {
        addRecord: function () {
            this.$emit('add', this.userId)
        },
        emitEdit: function (item) {
            let p = this.results.find(result => result.id === item.id)
            if (p) {
                this.$emit('edit', p)
            }
        },
        approveRecords: function () {
            let ids = []
            this.loading = true
            if (this.results) {
                this.results.forEach(function (record) {
                    ids.push(record.id)
                })
                this.restAdd(this.workTimeApproveUrl, {records: ids}, this.handleApproval, this.handleApprovalFail)
            } else {
                this.showAlert(this.$t('common.alert_update_failed'))
            }
        },

        handleApproval: function () {
            this.loading = false
            this.$emit('closeRecords')
        },

        handleApprovalFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },

        hasGap: function (first, second) {
            if (first && second) {
                let duration = this.getDurationInMinutes(first, second)
                return duration > 0 && duration <= 4 * 60
            }
            return false
        },

        initTableItems: function () {
            this.tableItems = []
            if (this.results) {
                let sorted = this.results.sort((a, b) => {
                    return a.approved_start_time > b.approved_start_time
                })
                let gapBack, gapFwd = false
                for (let i = 0; i < sorted.length; i++) {
                    let record = sorted[i]
                    let nextRecord = sorted[i + 1]
                    gapBack = !record.approved_start_time || gapFwd
                    gapFwd = !record.approved_end_time || nextRecord && this.hasGap(record.approved_end_time, nextRecord.approved_start_time)
                    this.tableItems.push({
                        id: record.id,
                        mode: record.mode && record.mode.name ? record.mode.name : '-',
                        costCenter: record.cost_center !== undefined ? record.cost_center.name : '-',
                        shift: this.getWorkShiftName(record.shift),
                        contractName: record.order ? record.order.contract.name : '-',
                        taskName: record.order ? record.order.task_type.name : '-',
                        start: record.start_time ? new Date(record.start_time).getTime() : '',
                        startTime: record.start_time ? this.toLocalTime(record.start_time) : '',
                        approved_start: record.approved_start_time ? new Date(record.approved_start_time).getTime() : '',
                        approvedStart: record.approved_start_time ? this.toLocalTime(record.approved_start_time) : '',
                        startLocation: this.getStartLocation(record),
                        end: record.end_time ? new Date(record.end_time).getTime() : '',
                        endTime: record.end_time ? this.toLocalTime(record.end_time) : '',
                        approved_end: record.approved_end_time ? new Date(record.approved_end_time).getTime() : '',
                        approvedEnd: record.approved_end_time ? this.toLocalTime(record.approved_end_time) : '',
                        endLocation: this.getEndLocation(record),
                        duration: record.approved_end_time !== undefined ? this.getDuration(record.approved_start_time, record.approved_end_time) : '',
                        approver: record.approver ? record.approver.last_name + ', ' + record.approver.first_name : '-',
                        note: record.note !== undefined ? record.note : '',
                    })
                }
            }
        },

        getRowClass(row) {
            const item = this.results.find(item => item.id === row.id)
            if (item.mode) {
                if (item.mode.work_type) {
                    return 'routa-table-row selected-row'
                }
                if (item.mode.absent_type) {
                    return 'routa-table-row expired-row'
                }
            }
            return 'routa-table-row'
        },

        getStartLocation: function (record) {
            let startLocation = '-'
            if (record.start_road_name) {
                startLocation = record.start_road_name
                if (record.start_city) {
                    startLocation += ', ' + record.start_city.name
                }
            }
            return startLocation
        },
        getEndLocation: function (record) {
            let endLocation = '-'
            if (record.end_road_name) {
                endLocation = record.end_road_name
                if (record.end_city) {
                    endLocation += ', ' + record.end_city.name
                }
            }
            return endLocation
        }
    }
}
</script>
